import { z } from "zod";
import { contentBlobSchema, encryptionModeSchema } from "../codec.js";
import { wrappedKeySchema } from "../spacekey.js";
import { isValidOrderKey } from "../order.js";

/**
 * Wire shapes — what the API sends and the client stores.
 *
 * Every row carries `revision`, the space-scoped monotonic counter that drives
 * delta sync, and a nullable `deletedAt`, because a hard delete would be
 * invisible to a client asking "what changed since revision N" (SPEC §7).
 */

export const uuid = z.uuid();

/**
 * `revision` is a Postgres bigint but travels as a number. A space would need
 * ~9e15 mutations to reach the safe-integer ceiling, which two people are not
 * going to manage.
 */
export const revisionSchema = z.number().int().nonnegative();

export const timestampSchema = z.iso.datetime();

export const orderKeySchema = z
  .string()
  .refine(isValidOrderKey, { message: "invalid order key" });

export const SPACE_ROLES = ["owner", "member"] as const;
export const spaceRoleSchema = z.enum(SPACE_ROLES);
export type SpaceRole = z.infer<typeof spaceRoleSchema>;

/**
 * Wishes are deliberately absent here. They were a list preset in the first
 * draft of the spec while §4.3 also described them as their own entity with a
 * link, a price and a bought/gifted status — two implementations of one idea,
 * visible as the same thing in two places in the UI.
 */
export const LIST_KINDS = ["todo", "shopping"] as const;
export const listKindSchema = z.enum(LIST_KINDS);
export type ListKind = z.infer<typeof listKindSchema>;

/** What one member may see about another. Never includes email. */
export const publicUserSchema = z.object({
  id: uuid,
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
});
export type PublicUser = z.infer<typeof publicUserSchema>;

/** The signed-in person's own record. */
export const selfUserSchema = publicUserSchema.extend({
  email: z.email(),
  createdAt: timestampSchema,
});
export type SelfUser = z.infer<typeof selfUserSchema>;

export const spaceSchema = z.object({
  id: uuid,
  content: contentBlobSchema,
  encryptionMode: encryptionModeSchema,
  /**
   * The space key, wrapped with a passphrase-derived key, or null in `none`
   * mode.
   *
   * It travels through the server and is stored by it, and the server can do
   * nothing with it — which is what lets a second device join a space knowing
   * only the passphrase. See `spacekey.ts` for how it is made and opened.
   */
  wrappedKey: wrappedKeySchema.nullable(),
  revision: revisionSchema,
  createdAt: timestampSchema,
});
export type Space = z.infer<typeof spaceSchema>;

export const spaceMemberSchema = z.object({
  spaceId: uuid,
  userId: uuid,
  role: spaceRoleSchema,
  joinedAt: timestampSchema,
  /**
   * How far this person has read. Null until they have opened the chat.
   *
   * On the wire because an unread count is otherwise unbuildable: the client
   * knows every message but not where the reader stopped.
   */
  lastReadMessageId: uuid.nullable(),
  user: publicUserSchema,
});
export type SpaceMember = z.infer<typeof spaceMemberSchema>;

/** A space as it appears in the signed-in user's own list of spaces. */
export const spaceSummarySchema = spaceSchema.extend({
  role: spaceRoleSchema,
  memberCount: z.number().int().positive(),
});
export type SpaceSummary = z.infer<typeof spaceSummarySchema>;

/**
 * The two things an invite code can do (SPEC §4.1).
 *
 * `space` lets someone into one particular space — the code the other person in
 * a household gets. `app` only opens an account, and the person who redeems it
 * lands in a brand-new space of their own; it grants no access to the space of
 * whoever minted it. Keeping these apart is what makes "come and use this
 * install" possible without also meaning "come and read my lists".
 */
export const INVITE_KINDS = ["space", "app"] as const;
export const inviteKindSchema = z.enum(INVITE_KINDS);
export type InviteKind = z.infer<typeof inviteKindSchema>;

export const inviteSchema = z.object({
  code: z.string(),
  /** Null for an `app` code: it is tied to no space by design. */
  spaceId: uuid.nullable(),
  /**
   * The role the redeemer ends up with. For an `app` code that is `owner`, of
   * the space registration creates for them — never a role in anyone else's.
   */
  role: spaceRoleSchema,
  createdAt: timestampSchema,
  expiresAt: timestampSchema,
  usedAt: timestampSchema.nullable(),
});
export type Invite = z.infer<typeof inviteSchema>;

/**
 * What an invitee may see before accepting. Deliberately almost nothing.
 *
 * A union rather than one shape with nullable halves, so that "an invite into a
 * space" and "an invite into the app" cannot be confused for one another: the
 * `app` variant has no `spaceId` field to read at all, rather than one that
 * happens to be null this time. There is nothing truthful to say about a space
 * for a code that names none, and inventing something — the inviter's space, a
 * placeholder name — would tell the invitee they are joining a household they
 * are not joining.
 */
export const invitePreviewSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("space"),
    code: z.string(),
    spaceId: uuid,
    role: spaceRoleSchema,
    encryptionMode: encryptionModeSchema,
    invitedBy: z.string(),
    expiresAt: timestampSchema,
  }),
  z.object({
    kind: z.literal("app"),
    code: z.string(),
    invitedBy: z.string(),
    expiresAt: timestampSchema,
  }),
]);
export type InvitePreview = z.infer<typeof invitePreviewSchema>;

export const listSchema = z.object({
  id: uuid,
  spaceId: uuid,
  kind: listKindSchema,
  content: contentBlobSchema,
  position: orderKeySchema,
  archivedAt: timestampSchema.nullable(),
  createdBy: uuid,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type List = z.infer<typeof listSchema>;

export const listItemSchema = z.object({
  id: uuid,
  spaceId: uuid,
  listId: uuid,
  parentId: uuid.nullable(),
  content: contentBlobSchema,
  done: z.boolean(),
  doneBy: uuid.nullable(),
  doneAt: timestampSchema.nullable(),
  dueAt: timestampSchema.nullable(),
  assigneeId: uuid.nullable(),
  position: orderKeySchema,
  createdBy: uuid,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type ListItem = z.infer<typeof listItemSchema>;

export const WISH_STATUSES = ["idea", "bought", "gifted"] as const;
export const wishStatusSchema = z.enum(WISH_STATUSES);
export type WishStatus = z.infer<typeof wishStatusSchema>;

/**
 * A thing somebody wants.
 *
 * The URL is optional and lives inside the content blob: "плед на диван" is as
 * much a wish as a product page, and the server has no business reading either.
 */
export const wishSchema = z.object({
  id: uuid,
  spaceId: uuid,
  content: contentBlobSchema,
  status: wishStatusSchema,
  position: orderKeySchema,
  claimedBy: uuid.nullable(),
  claimedAt: timestampSchema.nullable(),
  addedBy: uuid,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type Wish = z.infer<typeof wishSchema>;

/* ------------------------------------------------------------------ places */

/** One to five. Half-stars would be a scale nobody can hold in their head. */
export const RATING_MIN = 1;
export const RATING_MAX = 5;
export const ratingSchema = z.number().int().min(RATING_MIN).max(RATING_MAX);
export type Rating = z.infer<typeof ratingSchema>;

/**
 * A place on the map (SPEC §4.10).
 *
 * Where it is lives inside the content blob, unlike every other sortable fact
 * in this file — the reasoning is on `placeContentSchema`. The consequence is
 * worth stating here too: **this server cannot answer a geographic question**.
 * Not «what is near me», not «how far is that», not «who is close». Every one
 * of those is worked out on the phone, from rows it has already synced.
 */
export const placeSchema = z.object({
  id: uuid,
  spaceId: uuid,
  content: contentBlobSchema,
  /**
   * When somebody marked it as been-to, or null for one still on the list.
   *
   * A column and not a review, because the two answer different questions. A
   * rating says what one person thought; this says the pair has been. A place
   * can be visited and unrated — the evening was fine, nobody wrote anything —
   * and the map has to draw that differently from a place still to try.
   */
  visitedAt: timestampSchema.nullable(),
  createdBy: uuid,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type Place = z.infer<typeof placeSchema>;

/**
 * What one person thought of a place: a score, and words if they had any.
 *
 * One row per person per place — the shape of `messageReaction` and
 * `eventParticipant`, and for the same reason: an opinion belongs to whoever
 * holds it, and a second opinion must not overwrite the first.
 *
 * The score is a column while the words are a blob. A number out of five,
 * detached from the name of the place it is about, tells the server nothing it
 * did not already know from the row existing; the sentence would tell it
 * everything. Keeping the number outside means a client can sort and average
 * without opening anything, in either encryption mode.
 */
export const placeReviewSchema = z.object({
  /**
   * Its own, though the app never addresses it this way.
   *
   * Carried because the re-encryption pass walks rows by `id` — it is the one
   * mechanism that treats every content blob in the space alike, and a row it
   * cannot name is a row that stays readable after the switch to e2ee.
   */
  id: uuid,
  spaceId: uuid,
  placeId: uuid,
  userId: uuid,
  rating: ratingSchema,
  /** Null for a score somebody gave without saying anything. */
  content: contentBlobSchema.nullable(),
  updatedAt: timestampSchema,
  /** Set when the opinion is withdrawn, or when the place itself goes. */
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type PlaceReview = z.infer<typeof placeReviewSchema>;

/* ------------------------------------------------------- chat and topics */

export const TOPIC_KINDS = ["general", "thread"] as const;
export const topicKindSchema = z.enum(TOPIC_KINDS);
export type TopicKind = z.infer<typeof topicKindSchema>;

export const TOPIC_STATUSES = ["open", "resolved"] as const;
export const topicStatusSchema = z.enum(TOPIC_STATUSES);
export type TopicStatus = z.infer<typeof topicStatusSchema>;

/**
 * One entity, two presentations (SPEC §4.5): the space's single `general` topic
 * renders as a running conversation, a `thread` renders as a question with a
 * status. Splitting them into two tables would have duplicated messages,
 * reactions and unread tracking for no gain.
 */
/**
 * What a discussion can hang off.
 *
 * Narrower than `TAGGABLE_TYPES` on purpose. A thread about a thread is a thread
 * — there is nothing to anchor — and a discussion about a single message is
 * what `replyToId` already is.
 */
export const DISCUSSABLE_TYPES = ["wish", "item", "event", "place"] as const;
export const discussableTypeSchema = z.enum(DISCUSSABLE_TYPES);
export type DiscussableType = z.infer<typeof discussableTypeSchema>;

export const topicSchema = z.object({
  id: uuid,
  spaceId: uuid,
  kind: topicKindSchema,
  /**
   * What this discussion is about, or null for one that stands on its own.
   *
   * A comment thread on a wish is not a different kind of thing from a thread
   * in the chat — it is the same rows, asked a narrower question. Giving topics
   * a target rather than building a `comments` table is what lets editing,
   * replies, the resolved status, push notifications and the sync delta all
   * carry over untouched.
   */
  targetType: discussableTypeSchema.nullable(),
  targetId: uuid.nullable(),
  /**
   * Null for the `general` topic, which has no title anybody typed — its name is
   * a fixed label the client supplies.
   *
   * The server would otherwise have to author this blob, and a server-authored
   * plaintext blob is one an e2ee client refuses to decode, on purpose. This is
   * the only entity where content is optional, and that is why.
   */
  content: contentBlobSchema.nullable(),
  status: topicStatusSchema,
  /**
   * When somebody pinned this to the top of the list, or null if nobody has.
   *
   * Pinning is shared, not per device: the pair reads one list of topics, and a
   * pin one of them cannot see would be a different list rather than the same
   * one. It is also metadata rather than content — a flag and an order key in
   * the open, never inside the blob — so the server can sort by it without ever
   * opening what the topic is about (SPEC §3, decision 1).
   */
  pinnedAt: timestampSchema.nullable(),
  /**
   * Where it sits among the pinned ones. Null exactly when `pinnedAt` is.
   *
   * The same fractional key lists and wishes order by: dropping one pin between
   * two others writes that one row, so two people rearranging offline do not
   * renumber every sibling and collide on every one of them.
   */
  pinnedOrder: orderKeySchema.nullable(),
  createdBy: uuid,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type Topic = z.infer<typeof topicSchema>;

export const messageSchema = z.object({
  id: uuid,
  spaceId: uuid,
  topicId: uuid,
  replyToId: uuid.nullable(),
  content: contentBlobSchema,
  authorId: uuid,
  createdAt: timestampSchema,
  editedAt: timestampSchema.nullable(),
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type Message = z.infer<typeof messageSchema>;

/* ---------------------------------------------------------------- events */

/**
 * The ceiling, a week.
 *
 * Not an arbitrary round number: a reminder is a nudge about something in the
 * near future, and past a week it is a calendar in its own right — one that
 * would fire for everything on it at once the first time the server catches up.
 * Checked here rather than only in the settings screen, because the API is
 * reachable without it.
 */
export const MAX_REMINDER_LEAD_MINUTES = 7 * 24 * 60;

export const eventSchema = z.object({
  id: uuid,
  spaceId: uuid,
  content: contentBlobSchema,
  startsAt: timestampSchema,
  /** Null for an all-day entry, or an event with no stated finish. */
  endsAt: timestampSchema.nullable(),
  allDay: z.boolean(),
  /** RFC 5545 recurrence rule, or null for a one-off. */
  rrule: z.string().max(500).nullable(),
  /**
   * Minutes of notice for this event alone, or null for the notice this person
   * usually asks for.
   *
   * A notice belongs to the person (§4.7) and still does — this only overrides
   * their default. But one number cannot serve both kinds of event a household
   * has: a birthday wants two days, dinner at seven wants half an hour.
   */
  reminderLeadMinutes: z.number().int().min(0).max(MAX_REMINDER_LEAD_MINUTES).nullable(),
  createdBy: uuid,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type Event = z.infer<typeof eventSchema>;

/**
 * Who is in an event, and whether they are coming.
 *
 * `unanswered` is a real value rather than only the absence of a row: it is how
 * somebody takes back a "yes" without pretending they were never asked. The two
 * spellings mean the same thing and the client folds them — a member with no row
 * reads as `unanswered` too — so the screen never has to distinguish "has not
 * decided" from "has not been written down yet".
 */
export const EVENT_PARTICIPANT_STATUSES = ["going", "not_going", "unanswered"] as const;
export const eventParticipantStatusSchema = z.enum(EVENT_PARTICIPANT_STATUSES);
export type EventParticipantStatus = z.infer<typeof eventParticipantStatusSchema>;

/**
 * One person's answer about one event (SPEC §4.4).
 *
 * No id of its own: its identity is the pair it names, exactly as a tag link's
 * is the triple it links. That is what lets somebody answer, change their mind
 * and change it back without ever growing a second row — and it is why the
 * client keys these by `eventId:userId` rather than by an id.
 *
 * Only ever written for yourself. There is no field here for who did the
 * writing because there is only one possible answer, and a route that took one
 * would be a route that could be pointed at somebody else.
 */
export const eventParticipantSchema = z.object({
  spaceId: uuid,
  eventId: uuid,
  userId: uuid,
  status: eventParticipantStatusSchema,
  updatedAt: timestampSchema,
  /**
   * Set when the event itself was deleted, which soft-deletes its participants
   * under the same revision. A row that simply vanished would be invisible to a
   * client asking "what changed since N" (SPEC §7).
   */
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type EventParticipant = z.infer<typeof eventParticipantSchema>;

/**
 * The six answers a message can be given without words (SPEC §4.5).
 *
 * A fixed palette, not a picker. Two people do not need a thousand emoji, and
 * every one of them would be a decision to make while somebody is waiting for
 * an answer. Six fit a row on the narrowest phone this is built for, which is
 * the other half of the reason there are six.
 *
 * Adding one is safe; removing one is not — a reaction already given would stop
 * validating, and the row would fail to parse on every device at once. Retire a
 * face by leaving it here and taking it out of the picker.
 */
export const REACTIONS = ["❤️", "👍", "😂", "🥰", "😮", "😢"] as const;
export const reactionSchema = z.enum(REACTIONS);
export type Reaction = z.infer<typeof reactionSchema>;

/**
 * One person's reaction to one message (SPEC §4.5).
 *
 * The same shape as an event's participant and for the same reasons: no id of
 * its own, because its identity is the pair it names; only ever written for
 * yourself, because the route is `…/reactions/me` and the person comes off the
 * session; soft-deleted, because a row that simply vanished would be invisible
 * to a client asking what changed since revision N.
 *
 * **The emoji is stored in the clear, and that is a decision rather than an
 * oversight.** Everything a person *types* travels in a `ContentBlob` (SPEC §3),
 * but this is not typed — it is one of six buttons, exactly as a participant's
 * `going` is one of three. What the server learns is that this person answered
 * that message, and which of six faces they used; it already knew who wrote the
 * message and when. The cost of the alternative is what settles it: the whole
 * point of a reaction is that it lands the instant it is tapped, and a blob is
 * decrypted where deltas are applied, not where a bubble is drawn.
 */
export const messageReactionSchema = z.object({
  spaceId: uuid,
  messageId: uuid,
  userId: uuid,
  emoji: reactionSchema,
  updatedAt: timestampSchema,
  /** Set when it is taken back, or when the message itself is deleted. */
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type MessageReaction = z.infer<typeof messageReactionSchema>;

/* ------------------------------------------------------------------- push */

/**
 * What a notification can be about.
 *
 * A closed set rather than free text, because each one is a switch a person can
 * turn off, and a type nobody can name is a type nobody can silence.
 */
export const NOTIFICATION_TYPES = [
  "message.new",
  "item.assigned",
  "item.due",
  "event.soon",
  "wish.added",
  "place.added",
  "space.joined",
] as const;

export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

export const notificationSchema = z.object({
  id: uuid,
  spaceId: uuid,
  type: notificationTypeSchema,
  targetType: z.string().max(40).nullable(),
  targetId: uuid.nullable(),
  actorId: uuid.nullable(),
  /**
   * The short summary, written and encrypted by the client that acted.
   *
   * The server stores and forwards it without reading it (SPEC §3, decision 3),
   * which is what lets a push carry real text in an encrypted space.
   */
  preview: contentBlobSchema.nullable(),
  createdAt: timestampSchema,
  readAt: timestampSchema.nullable(),
});
export type Notification = z.infer<typeof notificationSchema>;

/** `HH:MM` in the person's own timezone. */
const clockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:MM");

/**
 * How much notice a reminder gives, when nobody has said otherwise.
 *
 * Fifteen minutes is the useful notice for two people sharing a calendar: long
 * enough to be a "leave now", short enough that it is still about the thing
 * that is happening. It is only a default now — the number itself is per person.
 */
export const DEFAULT_REMINDER_LEAD_MINUTES = 15;


/**
 * Minutes of notice before an event starts or a deadline falls.
 *
 * Zero is a real answer and not "unset": it means "tell me when it happens",
 * which is what somebody wants for a deadline they are working towards.
 */
const reminderLeadSchema = z
  .number()
  .int()
  .min(0)
  .max(MAX_REMINDER_LEAD_MINUTES)
  .default(DEFAULT_REMINDER_LEAD_MINUTES);

export const notificationPrefsSchema = z.object({
  /** Types this person wants to hear about. Anything absent stays silent. */
  enabled: z.array(notificationTypeSchema),
  /**
   * Quiet hours, inclusive of the start and exclusive of the end.
   *
   * A window that wraps midnight (22:00 → 08:00) is the normal case, so the
   * comparison cannot assume start < end.
   */
  quietFrom: clockTimeSchema.nullable(),
  quietTo: clockTimeSchema.nullable(),
  /** IANA name, so quiet hours mean the same thing after a flight. */
  timeZone: z.string().max(64),
  /**
   * How long before an event this person wants to hear about it.
   *
   * Per person rather than per space or per event: the same dinner is "leave in
   * fifteen minutes" for whoever lives upstairs and "an hour" for whoever has to
   * cross town, and neither of them should have to type that in twice.
   */
  eventLeadMinutes: reminderLeadSchema,
  /** The same, for a task's deadline. Kept apart because they are not the same
   *  kind of hurry: an event you have to travel to, a deadline you have to work
   *  towards, and one number for both would be wrong for one of them. */
  itemLeadMinutes: reminderLeadSchema,
});
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;

/* ------------------------------------------------------------------- tags */

/** What a tag can be attached to. */
export const TAGGABLE_TYPES = ["wish", "item", "event", "topic", "place"] as const;
export const taggableTypeSchema = z.enum(TAGGABLE_TYPES);
export type TaggableType = z.infer<typeof taggableTypeSchema>;

export const tagSchema = z.object({
  id: uuid,
  spaceId: uuid,
  content: contentBlobSchema,
  /** A token from a fixed palette, not a hex value — themes have to stay honest. */
  color: z.string().max(24).nullable(),
  createdBy: uuid,
  createdAt: timestampSchema,
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type Tag = z.infer<typeof tagSchema>;

export const tagLinkSchema = z.object({
  spaceId: uuid,
  tagId: uuid,
  targetType: taggableTypeSchema,
  targetId: uuid,
  revision: revisionSchema,
  deletedAt: timestampSchema.nullable(),
});
export type TagLink = z.infer<typeof tagLinkSchema>;

/* ------------------------------------------------------------ attachments */

export const ATTACHABLE_TYPES = ["wish", "message", "item", "event", "place"] as const;
export const attachableTypeSchema = z.enum(ATTACHABLE_TYPES);
export type AttachableType = z.infer<typeof attachableTypeSchema>;

export const attachmentSchema = z.object({
  id: uuid,
  spaceId: uuid,
  targetType: attachableTypeSchema,
  targetId: uuid,
  /** Path on our own origin. The bytes are never served from anywhere else. */
  url: z.string().max(300),
  mime: z.string().max(120),
  bytes: z.number().int().nonnegative(),
  content: contentBlobSchema,
  addedBy: uuid,
  createdAt: timestampSchema,
  deletedAt: timestampSchema.nullable(),
  revision: revisionSchema,
});
export type Attachment = z.infer<typeof attachmentSchema>;
